/**
 * src/store/slices/profileSlice.js
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import profileService from "../../services/profileService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// ── Thunks ──────────────────────────────────────────────────────────

export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const data = await profileService.getProfile();
      return data.data; // { name, email, role, specialization, employeeId, dateOfJoining, profilePicture }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const updatePassword = createAsyncThunk(
  "profile/updatePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const data = await profileService.changePassword({
        currentPassword,
        newPassword,
      });
      return data; // { success, message }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const updateProfilePicture = createAsyncThunk(
  "profile/updateProfilePicture",
  async (formData, { rejectWithValue }) => {
    try {
      const data = await profileService.uploadProfilePicture(formData);
      return data.data.profilePicture; // S3 URL string
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);
export const deleteProfilePicture = createAsyncThunk(
  "profile/deleteProfilePicture",
  async (_, { rejectWithValue }) => {
    try {
      const data = await profileService.deleteProfilePicture();
      return data; // { success, message }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);
// ── Initial State ────────────────────────────────────────────────────

const initialState = {
  profileData: null,

  // Loading flags
  isFetchingProfile: false,
  isUpdatingPassword: false,
  isUploadingPicture: false,
  isDeletingPicture: false,

  // Errors
  profileError: null,
  passwordError: null,
  pictureError: null,

  // Success flags
  passwordSuccess: false,
  pictureSuccess: false,
};

// ── Slice ────────────────────────────────────────────────────────────

const profileSlice = createSlice({
  name: "profile",
  initialState,

  reducers: {
    // Reset password success/error after modal closes
    clearPasswordState: (state) => {
      state.passwordError = null;
      state.passwordSuccess = false;
    },
    clearPictureState: (state) => {
      state.pictureError = null;
      state.pictureSuccess = false;
    },
  },

  extraReducers: (builder) => {
    // ── fetchProfile ──────────────────────────
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.isFetchingProfile = true;
        state.profileError = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isFetchingProfile = false;
        state.profileData = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isFetchingProfile = false;
        state.profileError = action.payload;
      });

    // ── updatePassword ────────────────────────
    builder
      .addCase(updatePassword.pending, (state) => {
        state.isUpdatingPassword = true;
        state.passwordError = null;
        state.passwordSuccess = false;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isUpdatingPassword = false;
        state.passwordSuccess = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isUpdatingPassword = false;
        state.passwordError = action.payload;
      });

    // ── updateProfilePicture ──────────────────
    builder
      .addCase(updateProfilePicture.pending, (state) => {
        state.isUploadingPicture = true;
        state.pictureError = null;
      })
      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        state.isUploadingPicture = false;
        state.pictureSuccess = true;
        // Update profilePicture URL in profileData
        if (state.profileData) {
          state.profileData.profilePicture = action.payload;
        }
      })
      .addCase(updateProfilePicture.rejected, (state, action) => {
        state.isUploadingPicture = false;
        state.pictureError = action.payload;
      });

      // ── deleteProfilePicture ──────────────────
builder
  .addCase(deleteProfilePicture.pending, (state) => {
    state.isDeletingPicture = true;
    state.pictureError = null;
  })
  .addCase(deleteProfilePicture.fulfilled, (state) => {
    state.isDeletingPicture = false;
    if (state.profileData) {
      state.profileData.profilePicture = null; // clear it in state
    }
  })
  .addCase(deleteProfilePicture.rejected, (state, action) => {
    state.isDeletingPicture = false;
    state.pictureError = action.payload;
  });
  },
});

export const { clearPasswordState, clearPictureState } = profileSlice.actions;
export default profileSlice.reducer;
